import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OfflineService } from '../../core/offline/offline.service';

/**
 * Connection state and the queue of changes waiting to reach the server.
 *
 * Stays invisible while everything is online and nothing is pending — the
 * indicator only earns its place on screen when there is something the user
 * needs to know.
 */
@Component({
  selector: 'app-offline-status',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, Button, DialogModule, TranslateModule],
  template: `
    @if (!offline.isOnline() || offline.pendingCount() > 0 || offline.unassignedCount() > 0) {
      <button
        type="button"
        (click)="dialogVisible.set(true)"
        class="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        [class.bg-warning-surface]="!offline.isOnline()"
        [class.text-warning-text]="!offline.isOnline()"
        [class.bg-primary-surface]="offline.isOnline()"
        [class.text-primary-text]="offline.isOnline()"
        [attr.aria-label]="ariaLabel()"
      >
        <i
          class="pi text-sm"
          [class.pi-wifi]="offline.isOnline()"
          [class.pi-exclamation-triangle]="!offline.isOnline()"
          aria-hidden="true"
        ></i>
        @if (!offline.isOnline()) {
          <span>{{ 'OFFLINE.BADGE' | translate }}</span>
        }
        @if (offline.pendingCount() > 0) {
          <span>{{ 'OFFLINE.PENDING' | translate: { count: offline.pendingCount() } }}</span>
        }
      </button>
    }

    <p-dialog
      [visible]="dialogVisible()"
      (visibleChange)="dialogVisible.set($event)"
      [modal]="true"
      [style]="{ width: '38rem' }"
      [header]="'OFFLINE.TITLE' | translate"
    >
      @if (offline.unassignedCount() > 0) {
        <p role="status" class="text-sm text-warning-text mb-4">{{ offline.unassignedCount() }} anciennes modifications sans propriétaire vérifiable nécessitent une reprise manuelle par l’administrateur.</p>
      }
      <p class="text-sm text-ink-muted mb-4">
        @if (offline.isOnline()) {
          {{ 'OFFLINE.EXPLAIN_ONLINE' | translate }}
        } @else {
          {{ 'OFFLINE.EXPLAIN_OFFLINE' | translate }}
        }
      </p>

      @if (offline.pendingCount() === 0) {
        <p class="text-sm text-ink-muted">{{ 'OFFLINE.EMPTY' | translate }}</p>
      }

      <ul class="flex flex-col gap-2">
        @for (entry of offline.pending(); track entry.id) {
          <li
            class="flex items-start justify-between gap-3 p-3 rounded-xl border"
            [class.border-danger-border]="!!entry.error"
            [class.bg-danger-surface]="!!entry.error"
            [class.border-hairline]="!entry.error"
          >
            <div class="min-w-0">
              <div class="text-sm font-semibold text-ink">
                {{ entry.label }}
              </div>
              <div class="text-xs text-ink-muted">
                {{ entry.createdAt | date: 'dd/MM/yyyy HH:mm' }}
              </div>
              @if (entry.error) {
                <div class="text-xs text-danger-text mt-1">{{ entry.error }}</div>
              }
            </div>
            @if (entry.error && entry.id !== undefined) {
              <p-button
                [label]="'OFFLINE.DISCARD' | translate"
                severity="danger"
                size="small"
                [text]="true"
                (onClick)="discard(entry.id!)"
              />
            }
          </li>
        }
      </ul>

      <ng-template #footer>
        <p-button
          [label]="'COMMON.CLOSE' | translate"
          severity="secondary"
          (onClick)="dialogVisible.set(false)"
        />
        <p-button
          [label]="'OFFLINE.SYNC_NOW' | translate"
          icon="pi pi-sync"
          [loading]="offline.syncing()"
          [disabled]="!offline.isOnline() || offline.pendingCount() === 0"
          (onClick)="syncNow()"
        />
      </ng-template>
    </p-dialog>
  `,
})
export class OfflineStatusComponent {
  protected readonly offline = inject(OfflineService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  readonly dialogVisible = signal(false);

  ariaLabel(): string {
    const state = this.translate.instant(
      this.offline.isOnline() ? 'OFFLINE.ONLINE' : 'OFFLINE.BADGE',
    );
    const count = this.offline.pendingCount();
    return count
      ? `${state} — ${this.translate.instant('OFFLINE.PENDING', { count })}`
      : state;
  }

  async syncNow() {
    const { sent, failed } = await this.offline.sync();
    this.messageService.add({
      severity: failed ? 'warn' : 'success',
      summary: this.translate.instant('OFFLINE.TITLE'),
      detail: failed
        ? this.translate.instant('OFFLINE.SYNC_PARTIAL', { sent, failed })
        : this.translate.instant('OFFLINE.SYNC_RESULT', { sent }),
      life: 5000,
    });
  }

  async discard(id: number) {
    await this.offline.discard(id);
  }
}
